import { useState, useEffect } from "react";
import validationQueue from "../utils/validationQueue.js";
import globalValidationManager from "../utils/globalValidationManager.js";
import { authenticatedFetch } from "../utils/authenticatedFetch.js";

export default function useStoreValidation(store, onValidationComplete) {
    // Local state from global validation manager
    const [validationResults, setValidationResults] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [validationProgress, setValidationProgress] = useState({
        currentPage: 0,
        totalPages: 0,
        currentPageName: '',
        isActive: false
    });

    // Queue-specific state
    const [isQueued, setIsQueued] = useState(false);
    const [queuePosition, setQueuePosition] = useState(0);

    // Subscribe to global validation state changes
    useEffect(() => {
        const updateLocalState = () => {
            const state = globalValidationManager.getValidationState(store.name);
            setValidationResults(state.validationResults);
            setIsValidating(state.isValidating);
            setValidationError(state.validationError);
            setValidationProgress(state.validationProgress);
        };

        // Initial state sync
        updateLocalState();

        // Subscribe to changes
        const unsubscribe = globalValidationManager.addListener(updateLocalState);

        return unsubscribe;
    }, [store.name]);

    // Listen to queue status changes
    useEffect(() => {
        const unsubscribe = validationQueue.addListener((status) => {
            const queueIndex = status.queuedStores.findIndex(
                s => s.storeName === store.name
            );

            if (queueIndex >= 0) {
                setIsQueued(true);
                setQueuePosition(queueIndex + 1);
            } else if (status.currentValidation?.store?.name === store.name) {
                setIsQueued(false);
                setQueuePosition(0);
            } else {
                setIsQueued(false);
                setQueuePosition(0);
            }
        });

        return unsubscribe;
    }, [store.name]);

    // Load stored validation results on component mount
    useEffect(() => {
        let isMounted = true;

        const loadStoredValidationResults = async () => {
            if (store.isTestStore) {
                return;
            }

            try {
                const response = await authenticatedFetch(`/api/stores/${encodeURIComponent(store.name)}/validation-results`);

                if (!response.ok) {
                    return;
                }

                const data = await response.json();

                if (isMounted && data.success && data.validationResult) {
                    globalValidationManager.setValidationResults(store.name, data.validationResult);
                }
            } catch (error) {
                if (isMounted) {
                    console.log('No stored validation results found for', store.name);
                }
            }
        };

        loadStoredValidationResults();

        return () => {
            isMounted = false;
        };
    }, [store.name]);

    // Queue validation request
    const runValidation = () => {
        validationQueue.addToQueue(
            store,
            null,
            () => {
                // On completion callback
                if (onValidationComplete) {
                    const state = globalValidationManager.getValidationState(store.name);
                    if (state.validationResults) {
                        onValidationComplete(store.name, state.validationResults);
                    }
                }
            },
            (error) => {
                console.error('Queue validation error:', error);
            }
        );
    };

    // Helper to manually set validation results (for backward compatibility)
    const setValidationResultsManually = (results) => {
        globalValidationManager.setValidationResults(store.name, results);
    };

    return {
        validationResults,
        setValidationResults: setValidationResultsManually,
        isValidating,
        validationError,
        isQueued,
        queuePosition,
        validationProgress,
        runValidation
    };
}
